import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from './icon';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'icon';
type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Lucide name rendered before `children` (or alone, for `icon` variant). */
  icon?: string;
  children?: ReactNode;
}

const BASE =
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap border-none transition-[background,transform,box-shadow,border-color,color] duration-150 active:scale-[0.97] disabled:cursor-not-allowed';

// Mirrors the mockup `.btn-*` rules (token-for-token).
const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'rounded-[var(--m-r-pill)] bg-[var(--m-brand)] px-[22px] py-[14px] text-[13px] font-bold uppercase tracking-[0.06em] text-[var(--m-fg-on-brand)] shadow-[var(--m-shadow-brand)] hover:bg-[var(--m-brand-hover)] active:bg-[var(--m-brand-press)] disabled:bg-[var(--m-border)] disabled:text-[var(--m-fg-subtle)] disabled:shadow-none',
  secondary:
    'rounded-[var(--m-r-pill)] border-[1.5px] border-[var(--m-border)] bg-[var(--m-surface)] px-5 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-[var(--m-fg)] hover:border-[var(--m-smoothie)] hover:bg-[var(--m-bg-alt)] disabled:opacity-50',
  ghost:
    'rounded-[var(--m-r-pill)] bg-transparent px-3 py-2 text-[14px] font-bold text-[var(--m-brand)] hover:bg-[var(--m-bg-alt)] disabled:opacity-50',
  icon: 'h-9 w-9 rounded-[var(--m-r-pill)] border border-[var(--m-border)] bg-[var(--m-surface)] text-[var(--m-fg-muted)] hover:bg-[var(--m-bg-alt)] hover:text-[var(--m-fg)] disabled:opacity-50',
};

// `.btn-sm` overrides padding/size on text variants only (not the icon square).
const SIZE_SM: Partial<Record<ButtonVariant, string>> = {
  primary: 'px-4 py-[10px] text-[12px]',
  secondary: 'px-4 py-[10px] text-[12px]',
};

/**
 * Mockup `.btn` family. `icon` variant is the 36px square; `sm` shrinks the
 * text variants. The default icon glyph size is 16 (14 when `sm`).
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  className = '',
  type,
  ...rest
}: ButtonProps): JSX.Element {
  const sizeClass = size === 'sm' ? (SIZE_SM[variant] ?? '') : '';
  const glyph = size === 'sm' ? 14 : variant === 'icon' ? 16 : 16;
  return (
    <button type={type ?? 'button'} className={`${BASE} ${VARIANT[variant]} ${sizeClass} ${className}`} {...rest}>
      {icon && <Icon name={icon} size={glyph} />}
      {children}
    </button>
  );
}
